import {
  backupSchema,
  hasPermission,
  newWorldNameSchema,
  worldActivateResultSchema,
  worldDeleteResultSchema,
  worldDownloadResultSchema,
  worldExtractResultSchema,
  worldListSchema,
  worldUploadResultSchema,
  type World,
  type WorldList,
} from '@hearth/shared';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  CircleOff,
  Download,
  Globe,
  Loader2,
  MoreHorizontal,
  RotateCcw,
  Trash2,
  Unplug,
  Upload,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState, type DragEvent } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { FormField } from '@/components/form-field';
import { EmptyState, SectionHeader } from '@/components/page-header';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { apiRequest, ApiRequestError } from '@/lib/api';
import { bytes, dateTime, timeAgo } from '@/lib/format';
import { errorMessage } from '@/lib/forms';
import { serverQuery } from '@/lib/server-queries';
import { useServerLive } from '@/lib/server-socket';
import { uploads, useUploads } from '@/lib/uploads';
import {
  dimensionLabel,
  isWorldArchive,
  suggestWorldName,
  WORLD_ARCHIVE_ACCEPT,
} from '@/lib/worlds';

export const Route = createFileRoute('/_app/server/$server/worlds')({ component: WorldsTab });

const worldsQuery = (server: string) =>
  queryOptions({
    queryKey: ['server', server, 'worlds'],
    queryFn: ({ signal }) =>
      apiRequest(`/api/client/servers/${server}/worlds`, { schema: worldListSchema, signal }),
  });

const worldUrl = (server: string, world: string, action: string) =>
  `/api/client/servers/${server}/worlds/${encodeURIComponent(world)}/${action}`;

type Asking = { kind: 'delete' | 'reset' | 'activate'; world: World } | null;

function WorldsTab() {
  const { server: id } = Route.useParams();
  const { data: server } = useSuspenseQuery(serverQuery(id));
  const list = useQuery(worldsQuery(id));
  const live = useServerLive(id);
  const queryClient = useQueryClient();
  const can = (p: Parameters<typeof hasPermission>[1]) => hasPermission(server.permissions, p);
  const [asking, setAsking] = useState<Asking>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: worldsQuery(id).queryKey });

  // The power state decides which actions are open; refresh when it changes.
  const liveState = live.state;
  useEffect(() => {
    if (liveState) void queryClient.invalidateQueries({ queryKey: worldsQuery(id).queryKey });
  }, [liveState, id, queryClient]);

  const download = useMutation({
    mutationFn: (world: World) =>
      apiRequest(worldUrl(id, world.name, 'download'), {
        method: 'POST',
        body: {},
        schema: worldDownloadResultSchema,
      }),
    onSuccess: (res) => {
      toast.success(`Downloading ${res.archive}`, {
        description: 'The archive stays in the server’s files until you delete it.',
      });
      window.location.assign(res.url);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (list.isPending)
    return (
      <div className="grid gap-3" aria-busy>
        <Skeleton className="h-24 rounded-xl" />
        <Skeleton className="h-24 rounded-xl" />
      </div>
    );
  if (list.isError) {
    if (list.error instanceof ApiRequestError && list.error.body?.code === 'worlds_unsupported')
      return (
        <EmptyState icon={CircleOff} title="Not available for this game">
          Hearth manages worlds for Minecraft: Java Edition servers so far.
        </EmptyState>
      );
    return (
      <Alert variant="destructive">
        <Unplug />
        <AlertTitle>Couldn’t load worlds</AlertTitle>
        <AlertDescription>{errorMessage(list.error)}</AlertDescription>
      </Alert>
    );
  }

  const data = list.data;
  const state = liveState ?? data.state;
  const stopped = state === 'offline';

  return (
    <div className="grid gap-6">
      <section>
        <SectionHeader
          title="Worlds"
          description={
            <>
              The game loads <span className="text-foreground font-medium">{data.levelName}</span>
              {data.levelNameSet
                ? ' (level-name in server.properties).'
                : ' — the game’s default, since server.properties doesn’t set level-name.'}
            </>
          }
        />
        {!data.activeExists && (
          <Alert className="mb-3">
            <Globe />
            <AlertTitle>“{data.levelName}” hasn’t been generated yet</AlertTitle>
            <AlertDescription>
              The game creates it the next time the server starts.
            </AlertDescription>
          </Alert>
        )}
        {data.worlds.length === 0 ? (
          <EmptyState icon={Globe} title="No worlds yet">
            Start the server once and the game generates one, or upload a world below.
          </EmptyState>
        ) : (
          <div className="grid gap-3">
            {data.worlds.map((w) => (
              <WorldCard
                key={w.name}
                world={w}
                stopped={stopped}
                can={can}
                downloading={download.isPending && download.variables?.name === w.name}
                onDownload={() => download.mutate(w)}
                onAsk={(kind) => setAsking({ kind, world: w })}
              />
            ))}
          </div>
        )}
        {!stopped && data.worlds.length > 0 && (
          <p className="text-muted-foreground mt-3 text-sm">
            The server is {state}. Stop it to switch or reset worlds, or to delete the active one.
            Downloads while it runs may miss changes the game hasn’t saved yet.
          </p>
        )}
      </section>

      {can('file.create') && can('file.archive') && (
        <UploadWorld server={id} list={data} onDone={() => void refresh()} />
      )}

      {asking?.kind === 'activate' && (
        <ActivateDialog
          server={id}
          world={asking.world}
          current={data.levelName}
          onClose={() => setAsking(null)}
          onDone={() => void refresh()}
        />
      )}
      {(asking?.kind === 'delete' || asking?.kind === 'reset') && (
        <DestroyDialog
          server={id}
          kind={asking.kind}
          world={asking.world}
          canBackup={can('backup.create')}
          onClose={() => setAsking(null)}
          onDone={() => void refresh()}
        />
      )}
    </div>
  );
}

function WorldCard({
  world,
  stopped,
  can,
  downloading,
  onDownload,
  onAsk,
}: {
  world: World;
  stopped: boolean;
  can: (p: Parameters<typeof hasPermission>[1]) => boolean;
  downloading: boolean;
  onDownload: () => void;
  onAsk: (kind: 'delete' | 'reset' | 'activate') => void;
}) {
  const canDownload = can('file.archive') && can('file.read-content');
  const canSwap = can('file.update') && !world.active;
  const canDelete = can('file.delete');
  const hasActions = canDownload || canSwap || canDelete;
  return (
    <Card className="flex-row items-start gap-4 p-4">
      <span
        aria-hidden
        className="bg-game-soft text-game grid size-10 shrink-0 place-items-center rounded-lg"
      >
        <Globe className="size-5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="truncate font-medium">{world.name}</h3>
          {world.active && <Badge variant="success">Active</Badge>}
        </div>
        <p className="text-muted-foreground mt-0.5 text-sm">
          {bytes(world.size)} · changed{' '}
          <time dateTime={world.modifiedAt} title={dateTime(world.modifiedAt)}>
            {timeAgo(world.modifiedAt)}
          </time>
        </p>
        <ul className="mt-2 flex flex-wrap gap-1.5" aria-label="Folders">
          {world.dimensions.map((d) => (
            <li
              key={d.folder}
              className="bg-elevated text-muted-foreground rounded-md border px-2 py-0.5 text-xs"
              title={`/${d.folder}: ${d.files} files`}
            >
              {dimensionLabel(world.name, d.folder)} · {bytes(d.size)}
            </li>
          ))}
        </ul>
      </div>
      {hasActions && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label={`Actions for ${world.name}`}>
              {downloading ? <Loader2 className="animate-spin" /> : <MoreHorizontal />}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {canDownload && (
              <DropdownMenuItem disabled={downloading} onSelect={onDownload}>
                <Download /> Download as .zip
              </DropdownMenuItem>
            )}
            {canSwap && (
              <DropdownMenuItem disabled={!stopped} onSelect={() => onAsk('activate')}>
                <Globe /> Make active{!stopped && ' (stop the server first)'}
              </DropdownMenuItem>
            )}
            {canDelete && (
              <>
                <DropdownMenuSeparator />
                {world.active && (
                  <DropdownMenuItem disabled={!stopped} onSelect={() => onAsk('reset')}>
                    <RotateCcw /> Reset…{!stopped && ' (stop the server first)'}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  variant="destructive"
                  disabled={world.active && !stopped}
                  onSelect={() => onAsk('delete')}
                >
                  <Trash2 /> Delete…{world.active && !stopped && ' (stop the server first)'}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </Card>
  );
}

function ActivateDialog({
  server,
  world,
  current,
  onClose,
  onDone,
}: {
  server: string;
  world: World;
  current: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const activate = useMutation({
    mutationFn: () =>
      apiRequest(worldUrl(server, world.name, 'activate'), {
        method: 'POST',
        body: {},
        schema: worldActivateResultSchema,
      }),
    onSuccess: () => {
      toast.success(`${world.name} is now the active world.`);
      onDone();
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Switch to “{world.name}”?</DialogTitle>
          <DialogDescription>
            This sets level-name in server.properties, so the game loads {world.name} the next time
            it starts. “{current}” isn’t changed or deleted; you can switch back any time.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={activate.isPending} onClick={() => activate.mutate()}>
            {activate.isPending && <Loader2 className="animate-spin" />}
            Make active
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const backupListSchema = z.object({ data: z.array(backupSchema) });

/** Starts a backup and resolves once it has finished; rejects if it failed. */
async function backupAndWait(server: string, world: string, onProgress: (s: string) => void) {
  const backup = await apiRequest(`/api/client/servers/${server}/backups`, {
    method: 'POST',
    body: { name: `Before resetting ${world}` },
    schema: backupSchema,
  });
  onProgress('Backing up…');
  for (;;) {
    await new Promise((r) => setTimeout(r, 2000));
    const { data } = await apiRequest(`/api/client/servers/${server}/backups`, {
      schema: backupListSchema,
    });
    const now = data.find((b) => b.uuid === backup.uuid);
    if (!now) throw new Error('The backup disappeared before it finished.');
    if (now.isSuccessful === true) return;
    if (now.isSuccessful === false)
      throw new Error('The backup failed, so nothing was deleted. See the Backups tab.');
  }
}

function DestroyDialog({
  server,
  kind,
  world,
  canBackup,
  onClose,
  onDone,
}: {
  server: string;
  kind: 'delete' | 'reset';
  world: World;
  canBackup: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [typed, setTyped] = useState('');
  const [backupFirst, setBackupFirst] = useState(kind === 'reset' && canBackup);
  const [progress, setProgress] = useState<string | null>(null);
  const run = useMutation({
    mutationFn: async () => {
      if (backupFirst) await backupAndWait(server, world.name, setProgress);
      setProgress(kind === 'reset' ? 'Resetting…' : 'Deleting…');
      return apiRequest(worldUrl(server, world.name, kind), {
        method: 'POST',
        body: { confirm: typed },
        schema: worldDeleteResultSchema,
      });
    },
    onSuccess: () => {
      toast.success(
        kind === 'reset'
          ? `${world.name} was deleted. The game generates a new world on the next start.`
          : `${world.name} was deleted.`,
      );
      onDone();
      onClose();
    },
    onError: (e) => {
      setProgress(null);
      toast.error(errorMessage(e));
    },
  });
  const verb = kind === 'reset' ? 'Reset' : 'Delete';
  return (
    <Dialog open onOpenChange={(o) => !o && !run.isPending && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {verb} “{world.name}”?
          </DialogTitle>
          <DialogDescription>
            {kind === 'reset'
              ? 'Everything in this world is deleted for good: builds, inventories, player progress and its other dimensions. The game generates a new world from the seed in server.properties the next time it starts.'
              : 'Everything in this world is deleted for good: builds, inventories, player progress and its other dimensions.'}
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (typed === world.name) run.mutate();
          }}
        >
          <div className="text-sm">
            <p className="mb-1.5 font-medium">
              These folders are deleted ({bytes(world.size)} in all):
            </p>
            <ul className="text-muted-foreground grid gap-0.5 font-mono text-xs">
              {world.dimensions.map((d) => (
                <li key={d.folder}>
                  /{d.folder} — {d.files} files, {bytes(d.size)}
                </li>
              ))}
            </ul>
          </div>
          {canBackup && (
            <div className="flex items-start gap-2">
              <Checkbox
                id="backup-first"
                checked={backupFirst}
                disabled={run.isPending}
                onCheckedChange={(v) => setBackupFirst(v === true)}
              />
              <Label htmlFor="backup-first" className="leading-snug font-normal">
                Make a backup of the whole server first, and only {verb.toLowerCase()} once it has
                finished
              </Label>
            </div>
          )}
          <FormField label={`Type ${world.name} to confirm`}>
            <Input
              autoComplete="off"
              spellCheck={false}
              value={typed}
              disabled={run.isPending}
              onChange={(e) => setTyped(e.target.value)}
            />
          </FormField>
          <DialogFooter>
            <Button type="button" variant="outline" disabled={run.isPending} onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={typed !== world.name || run.isPending}
            >
              {run.isPending && <Loader2 className="animate-spin" />}
              {progress ?? `${verb} world`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Upload: the API makes the new folder, the browser sends the archive into it with the
 * file manager's chunked uploader, and the API extracts it (hearthd refuses entries that
 * would land outside the folder) and moves the world into place.
 */
function UploadWorld({
  server,
  list,
  onDone,
}: {
  server: string;
  list: WorldList;
  onDone: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [dragging, setDragging] = useState(false);
  const [folder, setFolder] = useState<string | null>(null);
  const items = useUploads(server);
  const item = folder
    ? items.findLast((i) => i.directory === folder && i.name === file?.name)
    : undefined;
  const parsed = newWorldNameSchema.safeParse(name);
  const taken = list.worlds.some((w) => w.name.toLowerCase() === name.trim().toLowerCase());

  const choose = (f: File | undefined) => {
    if (!f) return;
    if (!isWorldArchive(f.name)) {
      toast.error('Upload a .zip, .tar, .tar.gz or .tar.xz archive of the world folder.');
      return;
    }
    setFile(f);
    setName(suggestWorldName(f.name));
  };

  const extract = useMutation({
    mutationFn: (args: { name: string; archive: string }) =>
      apiRequest(
        `/api/client/servers/${server}/worlds/uploads/${encodeURIComponent(args.name)}/extract`,
        { method: 'POST', body: { archive: args.archive }, schema: worldExtractResultSchema },
      ),
    onSuccess: (res) => {
      toast.success(`${res.world.name} is ready (${bytes(res.world.size)}).`, {
        description: res.discarded.length
          ? `Left out of the world and removed: ${res.discarded.join(', ')}.`
          : 'Make it active to play it.',
      });
      setFile(null);
      setName('');
      setFolder(null);
      onDone();
    },
    onError: (e) => {
      setFolder(null);
      toast.error(errorMessage(e));
      onDone();
    },
  });

  const start = useMutation({
    mutationFn: () =>
      apiRequest(`/api/client/servers/${server}/worlds/uploads`, {
        method: 'POST',
        body: { name: parsed.data },
        schema: worldUploadResultSchema,
      }),
    onSuccess: (res) => {
      const f = file!;
      const world = parsed.data!;
      setFolder(res.folder);
      uploads.add(server, res.folder, [f], () => {
        const done = uploads
          .snapshot()
          .findLast((i) => i.directory === res.folder && i.name === f.name);
        if (done?.status === 'done') extract.mutate({ name: world, archive: f.name });
        else {
          setFolder(null);
          if (done?.status === 'error')
            toast.error(`Upload failed: ${done.error ?? 'unknown error'}`, {
              description: `The folder ${res.folder} was created; delete it in Files if you don’t retry.`,
            });
        }
      });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const busy = start.isPending || extract.isPending || Boolean(folder);
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (!busy) choose(e.dataTransfer.files[0]);
  };

  return (
    <section>
      <SectionHeader
        title="Upload a world"
        description="A .zip or .tar archive of a world folder. It becomes a new world next to the others; nothing existing is replaced."
      />
      {!file ? (
        <button
          type="button"
          className={`hover:bg-elevated flex w-full flex-col items-center gap-2 rounded-xl border border-dashed px-6 py-10 text-center transition-colors ${dragging ? 'border-game bg-game-soft' : ''}`}
          onClick={() => input.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          <Upload className="text-muted-foreground size-5" aria-hidden />
          <span className="font-medium">Drop a world archive here, or choose one</span>
          <span className="text-muted-foreground text-sm">
            .zip, .tar, .tar.gz or .tar.xz. 7z and rar aren’t supported.
          </span>
        </button>
      ) : (
        <Card className="gap-4 p-4">
          <div className="flex items-center gap-3">
            <span className="min-w-0 flex-1 truncate text-sm">
              <span className="font-medium">{file.name}</span>{' '}
              <span className="text-muted-foreground">({bytes(file.size)})</span>
            </span>
            {!busy && (
              <Button
                variant="ghost"
                size="icon"
                aria-label="Choose another file"
                onClick={() => setFile(null)}
              >
                <X />
              </Button>
            )}
          </div>
          <form
            className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end"
            onSubmit={(e) => {
              e.preventDefault();
              if (parsed.success && !taken) start.mutate();
            }}
          >
            <FormField
              label="World name (its folder)"

              error={
                name && !parsed.success
                  ? parsed.error.issues[0]?.message
                  : taken
                    ? 'A world with this name already exists.'
                    : undefined
              }
            >
              <Input
                value={name}
                disabled={busy}
                autoComplete="off"
                onChange={(e) => setName(e.target.value)}
              />
            </FormField>
            <Button type="submit" disabled={busy || !parsed.success || taken}>
              {busy && <Loader2 className="animate-spin" />}
              {extract.isPending
                ? 'Extracting…'
                : item?.status === 'uploading'
                  ? `Uploading ${Math.floor((item.sent / Math.max(item.size, 1)) * 100)}%`
                  : 'Upload'}
            </Button>
          </form>
          {item?.status === 'uploading' && (
            <div
              className="bg-elevated h-1.5 overflow-hidden rounded-full"
              role="progressbar"
              aria-label="Upload progress"
              aria-valuenow={Math.floor((item.sent / Math.max(item.size, 1)) * 100)}
            >
              <div
                className="bg-game h-full transition-[width]"
                style={{ width: `${(item.sent / Math.max(item.size, 1)) * 100}%` }}
              />
            </div>
          )}
        </Card>
      )}
      <input
        ref={input}
        type="file"
        hidden
        accept={WORLD_ARCHIVE_ACCEPT}
        onChange={(e) => {
          choose(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </section>
  );
}
